import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Autocomplete,
  TextField,
  Select,
  MenuItem,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";
import SuiTypography from "components/SuiTypography";

//API
import SedesApi from "api/sedesApi";
import DatosBasicosApi from "api/datosbasicosApi";
import TarifasApi from "api/tarifasApi";

const EditTarifaModal = ({ open, onClose, tarifa, onSuccess }) => {
  const [sedes, setSedes] = useState([]);
  const [tiposProceso, setTiposProceso] = useState([]);
  const [sedeSelected, setSedeSelected] = useState([]);
  const [tipoProcesoSelected, setTipoProcesoSelected] = useState([]);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    sede: "",
    tipo_proceso: "",
    nombre: "",
    periodo: "",
    salario_minimo: "",
    salario_minimo_formatted: "",
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [sedesRes, tiposProcesoRes] = await Promise.all([
          SedesApi.fetchSedes(),
          DatosBasicosApi.fetchTiposProceso(),
        ]);
        setSedes(sedesRes.data || []);
        setTiposProceso(tiposProcesoRes.data || []);
      } catch (error) {
        console.error("Error al obtener los datos iniciales:", error);
        setError(obtenerMensajeError(error, "Error al cargar los datos iniciales."));
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (tarifa) {
      //alert("tarifa: " + JSON.stringify(tarifa));
      setSedeSelected(tarifa.sede_detalle);
      setTipoProcesoSelected(tarifa.tipo_proceso_detalle);
      setFormData({
        ...tarifa,
        sede: tarifa.sede,
        tipo_proceso: tarifa.tipo_proceso,
        salario_minimo_formatted: formatCurrency(tarifa.salario_minimo).formatted,
      });
    } else {
      setFormData({
        sede: "",
        tipo_proceso: "",
        nombre: "",
        periodo: "",
        salario_minimo: "",
        salario_minimo_formatted: "",
      });
    }
    setError(undefined);
  }, [tarifa]);

  // Periodos: año anterior, actual y siguiente (se conserva el periodo guardado de la tarifa)
  const anioActual = new Date().getFullYear();
  const periodos = [anioActual - 1, anioActual, anioActual + 1].map(String);
  if (formData.periodo && !periodos.includes(String(formData.periodo))) {
    periodos.unshift(String(formData.periodo));
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    const cleanValue = value;
    setFormData((prev) => ({
      ...prev,
      [name]: cleanValue,
    }));
  };

  const formatCurrency = (value) => {
    const numericValue = value.replace(/\D/g, "");
    const decimalValue = numericValue ? Number(numericValue) : 0;
    const formatter = new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return {
      formatted: formatter.format(numericValue || 0).replace(/\s/g, ""),
      value: decimalValue,
    };
  };

  const handleMoneyChange = (field) => (event) => {
    const rawValue = event.target.value;
    const { formatted, value } = formatCurrency(rawValue);
    setFormData((prev) => ({
      ...prev,
      [field]: value, // Guardamos solo el número limpio
      [`${field}_formatted`]: formatted, // Guardamos el valor formateado para mostrar
    }));
  };

  const resetState = () => {
    setError(undefined);
    setSedeSelected(null);
    setTipoProcesoSelected(null);
    setFormData({
      sede: "",
      tipo_proceso: "",
      nombre: "",
      periodo: "",
      salario_minimo: "",
      salario_minimo_formatted: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.sede === "") {
      setError("Debes seleccionar una Sede.");
      return;
    }
    if (formData.tipo_proceso === "") {
      setError("Debes seleccionar un Tipo de Proceso.");
      return;
    }
    if (!(Number(formData.salario_minimo) > 0)) {
      setError("Debes registrar un Salario Mínimo.");
      return;
    }
    try {
      //console.log("formData: " + JSON.stringify(formData));
      let newTarifa;
      if (tarifa) {
        newTarifa = await TarifasApi.updateTarifa(tarifa.id, formData);
      } else {
        newTarifa = await TarifasApi.createTarifa(formData);
      }
      if (newTarifa) {
        resetState();
        onSuccess();
      }
    } catch (error) {
      console.error("Error al guardar los datos de la Tarifa:", error);
      setError(obtenerMensajeError(error, "Error al guardar los datos de la Tarifa."));
    }
  };

  const hCClose = () => {
    resetState();
    onClose();
  };

  return (
    <Dialog open={open} onClose={hCClose} maxWidth="lg" fullWidth>
      <DialogTitle>{tarifa ? "Editar Tarifa" : "Nueva Tarifa"}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                Sede
              </SuiTypography>
              <Autocomplete
                disablePortal
                name="sede"
                options={sedes}
                getOptionLabel={(option) => option?.nombre || ""}
                sx={{ mb: 3 }}
                renderInput={(params) => <TextField {...params} />}
                value={sedeSelected}
                onChange={(event, value) => {
                  setSedeSelected(value);
                  setFormData((prev) => ({
                    ...prev,
                    sede: value ? value.id : "", // Asegura que si no hay valor, quede vacío
                  }));
                }}
              />
            </Grid>
            <Grid size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                Tipo Proceso
              </SuiTypography>
              <Autocomplete
                name="tipo_proceso"
                disablePortal
                options={tiposProceso}
                getOptionLabel={(option) => option?.nombre || ""}
                sx={{ mb: 3 }}
                renderInput={(params) => <TextField {...params} />}
                value={tipoProcesoSelected}
                onChange={(event, value) => {
                  setTipoProcesoSelected(value);
                  setFormData((prev) => ({
                    ...prev,
                    tipo_proceso: value ? value.id : "",
                  }));
                }}
              />
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={4}>
              <SuiTypography variant="h6" fontWeight="light">
                Periodo
              </SuiTypography>
              <Select
                name="periodo"
                value={formData.periodo}
                onChange={handleChange}
                fullWidth
                displayEmpty
              >
                <MenuItem value="">
                  <em>Seleccione un Periodo</em>
                </MenuItem>
                {periodos.map((periodo) => (
                  <MenuItem key={periodo} value={periodo}>
                    {periodo}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid size={4}>
              <SuiTypography variant="h6" fontWeight="light">
                Salario Minimo Legal Vigente
              </SuiTypography>
              <SuiInput
                value={formData.salario_minimo_formatted}
                onChange={handleMoneyChange("salario_minimo")}
                placeholder="SMLV"
                fullWidth
              />
            </Grid>
            <Grid size={4}></Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={4}></Grid>
            <Grid size={4}></Grid>
            <Grid size={4}></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SuiButton onClick={hCClose} buttonColor="light">
            Cancelar
          </SuiButton>
          <SuiButton type="submit" variant="gradient" buttonColor="dark">
            {tarifa ? "Actualizar" : "Crear"}
          </SuiButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

EditTarifaModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  tarifa: PropTypes.object,
  onSuccess: PropTypes.func.isRequired,
};

export default EditTarifaModal;
